import { apiClient } from './client'

// Types matching backend DTOs
export type MilestoneFocus = 'Elevation' | 'Distance' | 'UserChoice'

export type MilestoneDefinitionDto = {
  milestoneNumber: number
  weekNumber: number | null
  /**
   * Schedules the milestone relative to the plan's final week N
   * (milestone week = N - weeksFromEnd). Takes precedence over weekNumber.
   */
  weeksFromEnd?: number | null
  focus: MilestoneFocus
  minPercentage: number
  maxPercentage: number
  isPreHikeMilestone: boolean
  isHiddenFromUser: boolean
}

export type MilestoneConfigResponse = {
  milestones: MilestoneDefinitionDto[]
  preHikeMilestoneOffsetDays: number
  preHikeMilestonePercentage: number
  minimumWeeksForMilestones: number
  updatedAt: string | null
  updatedBy: string | null
}

export type MilestoneConfigRequest = {
  milestones: MilestoneDefinitionDto[]
  preHikeMilestoneOffsetDays: number
  preHikeMilestonePercentage: number
  minimumWeeksForMilestones: number
}

export type FocusTypeOption = {
  value: MilestoneFocus
  label: string
  description: string
}

// API functions
export async function getMilestoneConfig(): Promise<MilestoneConfigResponse> {
  const { data } = await apiClient.get<MilestoneConfigResponse>('/api/admin/config/milestones')
  return data
}

export async function updateMilestoneConfig(config: MilestoneConfigRequest): Promise<MilestoneConfigResponse> {
  const { data } = await apiClient.put<MilestoneConfigResponse>('/api/admin/config/milestones', config)
  return data
}

export async function resetMilestoneConfig(): Promise<MilestoneConfigResponse> {
  const { data } = await apiClient.post<MilestoneConfigResponse>('/api/admin/config/milestones/reset')
  return data
}

export async function getFocusTypes(): Promise<FocusTypeOption[]> {
  const { data } = await apiClient.get<FocusTypeOption[]>('/api/admin/config/milestones/focus-types')
  return data
}

// Helper to create default milestone config (matches backend defaults).
// Milestones are scheduled relative to the plan's final week N (week = N - weeksFromEnd).
export function getDefaultMilestones(): MilestoneDefinitionDto[] {
  return [
    { milestoneNumber: 1, weekNumber: null, weeksFromEnd: 8, focus: 'Elevation', minPercentage: 0.70, maxPercentage: 0.70, isPreHikeMilestone: false, isHiddenFromUser: false },
    { milestoneNumber: 2, weekNumber: null, weeksFromEnd: 7, focus: 'Distance', minPercentage: 0.70, maxPercentage: 0.70, isPreHikeMilestone: false, isHiddenFromUser: false },
    { milestoneNumber: 3, weekNumber: null, weeksFromEnd: 6, focus: 'Elevation', minPercentage: 0.75, maxPercentage: 0.75, isPreHikeMilestone: false, isHiddenFromUser: false },
    { milestoneNumber: 4, weekNumber: null, weeksFromEnd: 5, focus: 'Distance', minPercentage: 0.75, maxPercentage: 0.75, isPreHikeMilestone: false, isHiddenFromUser: false },
    { milestoneNumber: 5, weekNumber: null, weeksFromEnd: 4, focus: 'Elevation', minPercentage: 0.90, maxPercentage: 0.90, isPreHikeMilestone: false, isHiddenFromUser: false },
    { milestoneNumber: 6, weekNumber: null, weeksFromEnd: 3, focus: 'Distance', minPercentage: 0.90, maxPercentage: 0.90, isPreHikeMilestone: false, isHiddenFromUser: false },
    { milestoneNumber: 7, weekNumber: null, weeksFromEnd: 2, focus: 'Elevation', minPercentage: 1.00, maxPercentage: 1.00, isPreHikeMilestone: false, isHiddenFromUser: false },
    { milestoneNumber: 8, weekNumber: null, weeksFromEnd: 1, focus: 'Elevation', minPercentage: 0.50, maxPercentage: 0.50, isPreHikeMilestone: false, isHiddenFromUser: false }
  ]
}
